import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitBranch, Plus, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';

import type {
  DeveloperCheck,
  GitProvider,
  ProjectRepository,
  UpdateProjectRepositoryBody,
} from '@tasks/contracts';

import {
  getProjectRepository,
  testProjectRepository,
  testProjectRepositoryWrite,
  updateProjectRepository,
} from '../../api/integrations';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Select, type SelectOption } from '../../components/Select';
import { Skeleton } from '../../components/Skeleton';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import styles from './SettingsPages.module.css';

export interface ProjectRepositorySectionProps {
  readonly projectKey: string;
}

const PROVIDER_OPTIONS: readonly SelectOption<GitProvider>[] = [
  { value: 'github', label: 'GitHub' },
  { value: 'gitlab', label: 'GitLab' },
  { value: 'other', label: 'Другой (без PR)' },
];

function bodyOf(
  url: string,
  branch: string,
  provider: GitProvider,
  providerBaseUrl: string,
  token: string,
  writeToken: string,
  checks: readonly DeveloperCheck[],
): UpdateProjectRepositoryBody {
  const cleanChecks = checks
    .map((check) => ({
      name: check.name.trim(),
      command: check.command.trim(),
      timeoutSeconds: check.timeoutSeconds,
    }))
    .filter((check) => check.name !== '' && check.command !== '');
  return {
    url: url.trim(),
    ...(branch.trim() === '' ? {} : { branch: branch.trim() }),
    provider,
    ...(providerBaseUrl.trim() === '' ? {} : { providerBaseUrl: providerBaseUrl.trim() }),
    ...(token === '' ? {} : { token }),
    ...(writeToken === '' ? {} : { writeToken }),
    checks: cleanChecks,
  };
}

/**
 * Репозиторий проекта (docs/17 §5.5, docs/18 §5.5): read-only токен для
 * аналитика и write+PR токен с проверками для разработчика. Секреты
 * показываются только при вводе — на сервере хранится шифротекст.
 */
export function ProjectRepositorySection({ projectKey }: ProjectRepositorySectionProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [url, setUrl] = useState<string | null>(null);
  const [branch, setBranch] = useState<string | null>(null);
  const [provider, setProvider] = useState<GitProvider>('other');
  const [providerBaseUrl, setProviderBaseUrl] = useState('');
  const [token, setToken] = useState('');
  const [writeToken, setWriteToken] = useState('');
  const [checks, setChecks] = useState<DeveloperCheck[]>([]);

  const repositoryQuery = useQuery({
    queryKey: queryKeys.projects.repository(projectKey),
    queryFn: ({ signal }) => getProjectRepository(projectKey, signal),
    enabled: projectKey.length > 0,
  });

  useEffect(() => {
    if (repositoryQuery.data) {
      setUrl(repositoryQuery.data.url ?? '');
      setBranch(repositoryQuery.data.branch);
      setProvider(repositoryQuery.data.provider ?? 'other');
      setProviderBaseUrl(repositoryQuery.data.providerBaseUrl ?? '');
      setChecks(repositoryQuery.data.checks ?? []);
      setToken('');
      setWriteToken('');
    }
  }, [repositoryQuery.data]);

  const reportError = (cause: unknown): void => {
    const human = humanizeError(cause);
    toast.error(human.message, { code: human.code });
  };

  const applyResult = (data: ProjectRepository): void => {
    queryClient.setQueryData(queryKeys.projects.repository(projectKey), data);
    setUrl(data.url ?? '');
    setBranch(data.branch);
    setProvider(data.provider ?? 'other');
    setProviderBaseUrl(data.providerBaseUrl ?? '');
    setChecks(data.checks ?? []);
    setToken('');
    setWriteToken('');
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      updateProjectRepository(
        projectKey,
        bodyOf(url ?? '', branch ?? '', provider, providerBaseUrl, token, writeToken, checks),
      ),
    onSuccess: (data) => {
      applyResult(data);
      toast.success('Репозиторий проекта сохранён');
    },
    onError: reportError,
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      const saved = await updateProjectRepository(
        projectKey,
        bodyOf(url ?? '', branch ?? '', provider, providerBaseUrl, token, writeToken, checks),
      );
      queryClient.setQueryData(queryKeys.projects.repository(projectKey), saved);
      setToken('');
      setWriteToken('');
      return testProjectRepository(projectKey);
    },
    onSuccess: (result) => {
      toast.success(`Git доступен: ${result.branch} @ ${result.commit ?? '—'}`);
    },
    onError: reportError,
  });

  const testWriteMutation = useMutation({
    mutationFn: async () => {
      const saved = await updateProjectRepository(
        projectKey,
        bodyOf(url ?? '', branch ?? '', provider, providerBaseUrl, token, writeToken, checks),
      );
      queryClient.setQueryData(queryKeys.projects.repository(projectKey), saved);
      setToken('');
      setWriteToken('');
      return testProjectRepositoryWrite(projectKey);
    },
    onSuccess: (result) => {
      if (result.ok) {
        toast.success(
          `Разработчик: push ${result.canPush ? 'да' : 'нет'}, PR ${result.canCreatePr ? 'да' : 'нет'}`,
        );
      } else {
        toast.error('У Git-бота нет прав на push или создание PR');
      }
    },
    onError: reportError,
  });

  const patchCheck = (index: number, patch: Partial<DeveloperCheck>): void => {
    setChecks((current) =>
      current.map((check, i) => (i === index ? { ...check, ...patch } : check)),
    );
  };

  if (repositoryQuery.isPending) return <Skeleton variant="row" count={4} />;

  return (
    <section className={styles['section']}>
      <div className={styles['sectionHeader']}>
        <h2>Репозиторий проекта</h2>
        <span className={styles['hint']}>
          <GitBranch aria-hidden size={14} /> {branch === null || branch === '' ? 'develop' : branch}
        </span>
      </div>
      <p className={styles['hint']}>
        Аналитик получает read-only доступ, разработчик — отдельный write+PR токен
        и команды проверок. Токены хранятся зашифрованными и после сохранения не
        показываются.
      </p>
      {/* Без явных name/autocomplete Safari принимает форму за логин:
          password-поле есть, и «Ветку» он заполняет как имя пользователя. */}
      <form
        className={styles['form']}
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          saveMutation.mutate();
        }}
      >
        <Input
          name="repository-url"
          autoComplete="off"
          spellCheck={false}
          label="URL репозитория (HTTPS)"
          value={url ?? ''}
          placeholder="https://github.com/org/repo.git"
          onChange={(event) => setUrl(event.target.value)}
        />
        <Input
          name="repository-branch"
          autoComplete="off"
          spellCheck={false}
          label="Базовая ветка"
          value={branch ?? ''}
          placeholder="develop"
          onChange={(event) => setBranch(event.target.value)}
        />
        <Select
          label="Git-провайдер"
          options={PROVIDER_OPTIONS}
          value={provider}
          onChange={setProvider}
        />
        {provider !== 'other' ? (
          <Input
            name="repository-provider-base-url"
            autoComplete="off"
            spellCheck={false}
            label="API base URL провайдера (для self-hosted)"
            value={providerBaseUrl}
            placeholder="https://git.example.com"
            onChange={(event) => setProviderBaseUrl(event.target.value)}
          />
        ) : null}
        <Input
          name="repository-token"
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          label="Read-only токен аналитика"
          value={token}
          placeholder={
            repositoryQuery.data?.hasToken === true
              ? 'Токен задан — оставьте пустым'
              : 'Нужен для приватного репозитория'
          }
          onChange={(event) => setToken(event.target.value)}
        />
        <Input
          name="repository-write-token"
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          label="Write+PR токен разработчика"
          value={writeToken}
          placeholder={
            repositoryQuery.data?.hasWriteToken === true
              ? 'Токен задан — оставьте пустым'
              : 'Токен Git-бота с правами push и PR'
          }
          hint="Выпускается отдельному Git-боту; используется только runner'ом"
          onChange={(event) => setWriteToken(event.target.value)}
        />

        <div>
          <div className={styles['sectionHeader']}>
            <h3>Проверки разработчика</h3>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              icon={<Plus size={14} />}
              onClick={() =>
                setChecks((current) => [
                  ...current,
                  { name: `check-${current.length + 1}`, command: 'npm test', timeoutSeconds: 600 },
                ])
              }
            >
              Добавить
            </Button>
          </div>
          <p className={styles['hint']}>
            Команды выполняются в песочнице после правок. До пяти команд; при
            пустом списке PR создаётся без проверок.
          </p>
          {checks.map((check, index) => (
            <div key={index} className={styles['checkRow']}>
              <Input
                label="Имя"
                value={check.name}
                onChange={(event) => patchCheck(index, { name: event.target.value })}
              />
              <Input
                label="Команда"
                value={check.command}
                onChange={(event) => patchCheck(index, { command: event.target.value })}
              />
              <Input
                label="Таймаут, с"
                type="number"
                value={String(check.timeoutSeconds)}
                onChange={(event) =>
                  patchCheck(index, {
                    timeoutSeconds: Math.max(1, Number(event.target.value) || 1),
                  })
                }
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Удалить проверку ${check.name}`}
                onClick={() => setChecks((current) => current.filter((_, i) => i !== index))}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </div>

        <div className={styles['actions']}>
          <Button type="submit" variant="secondary" loading={saveMutation.isPending}>
            Сохранить
          </Button>
          <Button
            type="button"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={testMutation.isPending}
            onClick={() => testMutation.mutate()}
          >
            Проверить доступ
          </Button>
          <Button
            type="button"
            variant="ghost"
            icon={<ShieldCheck size={14} />}
            loading={testWriteMutation.isPending}
            onClick={() => testWriteMutation.mutate()}
          >
            Проверить запись
          </Button>
        </div>
      </form>
    </section>
  );
}
